import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Navbar from "./Navbar";
import Footer from "./Footer";
import logo from "../logo.png";
import i4c from "../i4c.png";
import { isMobilePhoneBrowser } from "../utils/deviceDetection";

const JusticeMission = () => {
  const navigate = useNavigate();

  const APP_LOADED_AT_KEY = "app_loaded_at_ms";
  const RELOAD_DAYS = 1;
  const RELOAD_MS = RELOAD_DAYS * 24 * 60 * 60 * 1000;

  const [showUpdatePopup, setShowUpdatePopup] = useState(false);

  const hostname = window.location.hostname.toLowerCase();

  // Store the date/time when the web app is fetched/loaded
  useEffect(() => {
    try {
      const now = Date.now();
      const existing = localStorage.getItem(APP_LOADED_AT_KEY);

      if (!existing || Number.isNaN(Number(existing))) {
        localStorage.setItem(APP_LOADED_AT_KEY, String(now));
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  // A valid (non-expired) JWT already in localStorage lets a mobile-web user
  // skip straight to the case list, since browsers have no biometric/secure-store
  // equivalent to mirror the app's silent auto-login.
  const hasValidSession = () => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) return false;
      const decoded = jwtDecode(token);
      return Boolean(decoded?.exp) && decoded.exp > Date.now() / 1000;
    } catch {
      return false;
    }
  };

  const goToMobileExperience = () => {
    navigate(hasValidSession() ? "/m/cases" : "/m/login");
  };

  const checkUpdateRequired = () => {
    try {
      const stored = localStorage.getItem(APP_LOADED_AT_KEY);
      const loadedAt = stored ? Number(stored) : NaN;

      if (!Number.isNaN(loadedAt)) {
        const ageMs = Date.now() - loadedAt;

        // If app was loaded more than 6 hours ago, force update popup
        if (ageMs > RELOAD_MS) {
          setShowUpdatePopup(true);
          return true;
        }
      }
    } catch {
      // ignore storage errors and proceed normally
    }
    return false;
  };

  const handleContinue = () => {
    if (checkUpdateRequired()) return;

    if (isMobilePhoneBrowser()) {
      goToMobileExperience();
    } else {
      navigate("/login");
    }
  };

  // Explicit one-time overrides — never persisted, every fresh visit re-detects.
  const handleGoDesktop = () => {
    if (checkUpdateRequired()) return;
    navigate("/login");
  };

  const handleGoMobile = () => {
    if (checkUpdateRequired()) return;
    goToMobileExperience();
  };

  const handleGetLatestVersion = () => {
    try {
      // Update timestamp so after refresh user isn't immediately prompted again
      localStorage.setItem(APP_LOADED_AT_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    window.location.reload();
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        color: "black",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <Navbar />

      <main
        style={{
          flex: "1",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <img
          src={hostname === "i4c.legalapp.in" ? i4c : logo}
          alt="LegalApp logo"
          style={{
            width: hostname === "i4c.legalapp.in" ? "240px" : "240px",
            height: "auto",
            marginBottom: "14px",
            objectFit: "contain",
          }}
        />

        {hostname !== "i4c.legalapp.in" && (
          <h1 style={{ marginBottom: "15px", fontSize: "24px", fontWeight: "bold" }}>
            In the interest of Justice
          </h1>
        )}

        <p
          style={{
            fontSize: "20px",
            maxWidth: hostname === "i4c.legalapp.in" ? "800px" : "800px",
            lineHeight: "1.5",
            textAlign: "justify",
          }}
        >
          Empowering Police with an <strong>AI-powered assistant</strong> for faster and stronger investigations.
          {/* {hostname === "i4c.legalapp.in" ? (
            <>
              Empowering Police with an <strong>AI-powered assistant</strong> for faster and stronger investigations.
            </>
          ) : (
            <>
              Empowering <strong>Police</strong> and <strong>Advocates</strong> with AI-powered case
              intelligence for stronger Investigation and Advocacy.
            </>
          )} */}
        </p>

        <div style={{ marginTop: "20px" }}>
          <button
            onClick={handleContinue}
            style={{
              fontSize: "18px",
              color: "#094268",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: "bold",
            }}
          >
            Continue <span style={{ fontSize: "20px" }}>→</span>
          </button>
        </div>

        <div style={{ marginTop: "12px", display: "flex", gap: "20px" }}>
          <button
            onClick={handleGoDesktop}
            style={{
              fontSize: "14px",
              color: "#094268",
              background: "none",
              border: "1.5px solid currentColor",
              borderRadius: "999px",
              padding: "6px 16px",
              cursor: "pointer",
            }}
          >
            Desktop
          </button>
          <button
            onClick={handleGoMobile}
            style={{
              fontSize: "14px",
              color: "#094268",
              background: "none",
              border: "1.5px solid currentColor",
              borderRadius: "999px",
              padding: "6px 16px",
              cursor: "pointer",
            }}
          >
            Mobile
          </button>
        </div>
      </main>

      <Footer />

      {/* Mandatory update popup (no cancel option) */}
      {showUpdatePopup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "520px",
              background: "white",
              borderRadius: "12px",
              padding: "18px 18px 14px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              textAlign: "left",
            }}
          >
            <div style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "8px" }}>
              Update Required
            </div>

            <div style={{ fontSize: "15px", lineHeight: "1.45", marginBottom: "14px" }}>
              A new version of the application is available. To proceed, please get the latest version.
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={handleGetLatestVersion}
                style={{
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: "1px solid #094268",
                  background: "#094268",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Get Latest Version
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JusticeMission;